import { useFeatureFlag } from '../../../../../../../hooks/useFeatureFlag';
import useGeneralSettingsStore from '../../../../../../../stores/GeneralSettings/useGeneralSettingsStore';
import { useApplicationContext } from '../../../../../../context/ApplicationContext/ApplicationContext';
import { BrandIntegration } from '../../../../../../context/ApplicationContext/applicationContext.types';
import { UsedInOfflineMode } from '../../../../../../offline/types/UsedInOfflineMode';
import { useAbility } from '../../../../../../users/AbilityContext';
import { Role } from '../../../../../actions/types/actions.types';
import useOrderBillingForecast from '../../../../../conditions/hooks/useOrderBillingForecast';
import useOrderDefinitions, {
  OrderDefinitions,
} from '../../../../../conditions/hooks/useOrderDefinitions';
import { OrderCondition } from '../../../../../conditions/interfaces/orderCondition.interface';
import { getOrderAvailability } from '../../../../../conditions/orderCondition.utils';
import { Order, OrderTransportCondition } from '../../../../../order.types';

interface UseOrderConditionFieldsVisibilityParams extends UsedInOfflineMode {
  order: Order;
  condition: OrderCondition;
}

interface HasVisibleOrderDefinitionsParams {
  isBrand: boolean;
  orderDefinitions: OrderDefinitions;
}

const getHasVisibleOrderDefinitions = ({
  isBrand,
  orderDefinitions,
}: HasVisibleOrderDefinitionsParams) =>
  isBrand ||
  orderDefinitions.shippingCompanyIntegration ||
  orderDefinitions.showEmitInvoice ||
  orderDefinitions.showLocals ||
  orderDefinitions.orderTransportCondition !== OrderTransportCondition.NONE;

interface HasOrderInvoicesAccessParams {
  isEnableInvoices: boolean;
  integrations?: BrandIntegration[];
  canReadInvoices: boolean;
}

const getHasOrderInvoicesAccess = ({
  isEnableInvoices,
  integrations,
  canReadInvoices,
}: HasOrderInvoicesAccessParams) =>
  isEnableInvoices && (integrations ?? []).length > 0 && canReadInvoices;

const useOrderConditionFieldsVisibility = ({
  order,
  condition,
  isOfflineMode = false,
}: UseOrderConditionFieldsVisibilityParams) => {
  const { isSeller, isAdminBrand, isBrand, integrations, userRole } = useApplicationContext();
  const ability = useAbility();
  const { generalSettings } = useGeneralSettingsStore();
  const { isVoucherEnabled, isEnableInvoices } = useFeatureFlag();
  const orderDefinitions = useOrderDefinitions();

  const { showOrderBillingForecastDate, isReadOnlyOrderBillingForecastDate } =
    useOrderBillingForecast({
      orderCondition: condition,
      orderSplitSettings: order.splitSettings,
      ruleForPreOrder: generalSettings.billingForecastRuleForPreOrder,
      ruleForReadyForDelivery: generalSettings.billingForecastRuleForReadyForDelivery,
    });

  const hasOnlineOnlyFields = !isOfflineMode;
  const shouldHideOrderObservation = !!generalSettings.rolesToHideOrderObservation?.includes(
    userRole as Role,
  );
  const isShippingCostVisible =
    hasOnlineOnlyFields && generalSettings.showOrderConditionShippingCost;
  const hasVisibleOrderDefinitions = getHasVisibleOrderDefinitions({ isBrand, orderDefinitions });
  const hasOrderInvoicesAccess = getHasOrderInvoicesAccess({
    isEnableInvoices,
    integrations,
    canReadInvoices: ability.can('read', 'invoices'),
  });

  return {
    availability: getOrderAvailability(condition.orderType),
    isDetailsSectionVisible: hasOnlineOnlyFields,
    isBillingForecastDateVisible: hasOnlineOnlyFields && showOrderBillingForecastDate,
    isBillingForecastDateReadOnly:
      isReadOnlyOrderBillingForecastDate || (!isSeller && !isAdminBrand),
    isVoucherVisible: hasOnlineOnlyFields && isVoucherEnabled,
    isDeliverySectionVisible: hasOnlineOnlyFields,
    isShippingCostVisible,
    isObservationVisible: !shouldHideOrderObservation,
    isInternalObservationVisible: hasOnlineOnlyFields && isBrand && !shouldHideOrderObservation,
    isMoreConditionsVisible: hasOnlineOnlyFields && hasVisibleOrderDefinitions,
    isOrderInvoicesVisible: hasOnlineOnlyFields && hasOrderInvoicesAccess,
  };
};

export default useOrderConditionFieldsVisibility;
